/**
 * Same-origin Paya ID unlock popup (#2217).
 *
 * The opener claims the window on click (before any await) so the browser
 * still treats it as a user gesture. After the provider returns, the popup
 * asks this window for the lock identity over a source-checked postMessage
 * handshake. Tokens travel only in the result postMessage; they are not
 * written to storage, the URL, or logs.
 *
 * Do not classify the returning callback with `window.name`. Browsers reset
 * it on a different-origin navigation (the Paya ID hop).
 */

export const UNLOCK_POPUP_WINDOW_NAME = 'os-buddy-session-unlock';
export const UNLOCK_POPUP_SESSION_KEY = 'os-buddy-portal-unlock';
export const UNLOCK_POPUP_MESSAGE_TYPE = 'os-buddy-portal-unlock-result';
export const UNLOCK_POPUP_HELLO_TYPE = 'os-buddy-portal-unlock-hello';
export const UNLOCK_POPUP_CHALLENGE_TYPE = 'os-buddy-portal-unlock-challenge';
export const UNLOCK_POPUP_TIMEOUT_MS = 130_000;
export const UNLOCK_POPUP_HANDSHAKE_TIMEOUT_MS = 2_000;
export const UNLOCK_POPUP_FEATURES = 'width=480,height=720';

export type UnlockPopupIdentity = {
  sessionFamilyId: string;
  lockGeneration: number;
  userId: string;
  tenantId: string;
  sessionEpoch: number;
};

export type UnlockPopupHelloMessage = {
  type: typeof UNLOCK_POPUP_HELLO_TYPE;
};

export type UnlockPopupChallengeMessage = UnlockPopupIdentity & {
  type: typeof UNLOCK_POPUP_CHALLENGE_TYPE;
  nonce: string;
};

export type UnlockPopupSuccessMessage = UnlockPopupIdentity & {
  type: typeof UNLOCK_POPUP_MESSAGE_TYPE;
  status: 'success';
  nonce: string;
  accessToken: string;
  refreshToken: string;
};

export type UnlockPopupErrorMessage = {
  type: typeof UNLOCK_POPUP_MESSAGE_TYPE;
  status: 'error';
};

export type UnlockPopupMessage = UnlockPopupSuccessMessage | UnlockPopupErrorMessage;

export type UnlockPopupWireMessage =
  | UnlockPopupMessage
  | UnlockPopupHelloMessage
  | UnlockPopupChallengeMessage;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

function parseIdentity(record: Record<string, unknown>): UnlockPopupIdentity | null {
  if (
    !isNonEmptyString(record.sessionFamilyId) ||
    !isNonEmptyString(record.userId) ||
    !isNonEmptyString(record.tenantId) ||
    typeof record.lockGeneration !== 'number' ||
    !Number.isInteger(record.lockGeneration) ||
    record.lockGeneration <= 0 ||
    typeof record.sessionEpoch !== 'number' ||
    !Number.isInteger(record.sessionEpoch)
  ) {
    return null;
  }
  return {
    sessionFamilyId: record.sessionFamilyId,
    lockGeneration: record.lockGeneration,
    userId: record.userId,
    tenantId: record.tenantId,
    sessionEpoch: record.sessionEpoch,
  };
}

export function createUnlockPopupNonce(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function readUnlockPopupNonce(): string | null {
  try {
    const value = sessionStorage.getItem(UNLOCK_POPUP_SESSION_KEY);
    if (typeof value !== 'string' || value === '') return null;
    return value;
  } catch {
    return null;
  }
}

export function markUnlockPopupSession(popup: Window, nonce: string): void {
  try {
    popup.sessionStorage.setItem(UNLOCK_POPUP_SESSION_KEY, nonce);
  } catch {
    // about:blank / blocked storage: the handshake still identifies the window.
  }
}

export function prepareUnlockPopup(popup: Window): string {
  const nonce = createUnlockPopupNonce();
  markUnlockPopupSession(popup, nonce);
  return nonce;
}

/**
 * Snapshot of whether this window is the unlock popup, set once the
 * handshake has settled. `null` means not yet classified.
 */
let capturedUnlockPopup: boolean | null = null;

export function clearUnlockPopupSession(): void {
  capturedUnlockPopup = null;
  try {
    sessionStorage.removeItem(UNLOCK_POPUP_SESSION_KEY);
  } catch {
    // Private window: the in-memory handshake result is enough.
  }
}

export function hasSameOriginOpener(): boolean {
  try {
    const opener = window.opener;
    if (opener === null || opener === undefined) return false;
    if (opener.closed === true) return false;
    return opener.location.origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Snapshot whether this window is the unlock popup. Call once the handshake
 * has settled (or immediately when there is no opener to ask). The hook's
 * `onSuccess` and the handoff page must read this same snapshot.
 */
export function captureUnlockPopupClassification(
  challenge: UnlockPopupChallengeMessage | null,
): boolean {
  capturedUnlockPopup = challenge !== null || readUnlockPopupNonce() !== null;
  return capturedUnlockPopup;
}

/**
 * True when this browsing context is (or may still be) the unlock popup.
 *
 * Classification is a lifecycle, not "any live same-origin opener":
 * - Before handshake: a live same-origin opener (or the per-window marker)
 *   is enough to refuse a store write. `window.name` is not consulted.
 * - After handshake: a claimed challenge or the session marker is unlock;
 *   an unmarked opener that never answered is ordinary login.
 */
export function isUnlockPopupWindow(): boolean {
  if (capturedUnlockPopup !== null) return capturedUnlockPopup;
  return readUnlockPopupNonce() !== null || hasSameOriginOpener();
}

export function openUnlockPopup(): Window | null {
  try {
    const popup = window.open('about:blank', UNLOCK_POPUP_WINDOW_NAME, UNLOCK_POPUP_FEATURES);
    return popup ?? null;
  } catch {
    return null;
  }
}

export function isSafeUnlockStartUrl(url: string): boolean {
  if (url.startsWith('/api/v1/auth/oidc/start')) {
    if (url.startsWith('//') || url.startsWith('/\\')) return false;
    return true;
  }
  try {
    const parsed = new URL(url, window.location.origin);
    return (
      parsed.origin === window.location.origin &&
      parsed.pathname === '/api/v1/auth/oidc/start'
    );
  } catch {
    return false;
  }
}

export function assignUnlockPopupLocation(popup: Window, startUrl: string): void {
  try {
    popup.location.assign(startUrl);
  } catch {
    try {
      popup.location.href = startUrl;
    } catch {
      // The opener still has the fallback redirect if this window is gone.
    }
  }
}

export function parseUnlockPopupHello(value: unknown): UnlockPopupHelloMessage | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (record.type !== UNLOCK_POPUP_HELLO_TYPE) return null;
  return { type: UNLOCK_POPUP_HELLO_TYPE };
}

export function parseUnlockPopupChallenge(value: unknown): UnlockPopupChallengeMessage | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (record.type !== UNLOCK_POPUP_CHALLENGE_TYPE) return null;
  if (!isNonEmptyString(record.nonce)) return null;
  const identity = parseIdentity(record);
  if (identity === null) return null;
  return {
    type: UNLOCK_POPUP_CHALLENGE_TYPE,
    nonce: record.nonce,
    ...identity,
  };
}

export function parseUnlockPopupMessage(value: unknown): UnlockPopupMessage | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (record.type !== UNLOCK_POPUP_MESSAGE_TYPE) return null;
  if (record.status === 'error') {
    return { type: UNLOCK_POPUP_MESSAGE_TYPE, status: 'error' };
  }
  if (record.status !== 'success') return null;
  if (!isNonEmptyString(record.accessToken) || !isNonEmptyString(record.refreshToken)) {
    return null;
  }
  if (!isNonEmptyString(record.nonce)) return null;
  const identity = parseIdentity(record);
  if (identity === null) return null;
  return {
    type: UNLOCK_POPUP_MESSAGE_TYPE,
    status: 'success',
    nonce: record.nonce,
    accessToken: record.accessToken,
    refreshToken: record.refreshToken,
    ...identity,
  };
}

export function identitiesMatch(
  message: UnlockPopupSuccessMessage,
  expected: UnlockPopupIdentity,
): boolean {
  return (
    message.sessionFamilyId === expected.sessionFamilyId &&
    message.lockGeneration === expected.lockGeneration &&
    message.userId === expected.userId &&
    message.tenantId === expected.tenantId &&
    message.sessionEpoch === expected.sessionEpoch
  );
}

export function refreshTokensMatch(returned: string, locked: string): boolean {
  return returned !== '' && locked !== '' && returned === locked;
}

export function postUnlockPopupMessage(target: Window, message: UnlockPopupWireMessage): void {
  target.postMessage(message, window.location.origin);
}

export function postUnlockPopupResultToOpener(message: UnlockPopupMessage): void {
  try {
    const opener = window.opener;
    if (opener === null || opener === undefined) return;
    if (opener.location.origin !== window.location.origin) return;
    postUnlockPopupMessage(opener, message);
  } catch {
    // Cross-origin or closed opener: do not deliver.
  }
}

function rememberUnlockPopupNonce(nonce: string): void {
  try {
    sessionStorage.setItem(UNLOCK_POPUP_SESSION_KEY, nonce);
  } catch {
    // Handshake identity is already in memory for this page.
  }
}

/**
 * Ask the opener for the lock identity it captured. Resolves immediately
 * when there is no same-origin opener (ordinary sign-in). Captures the
 * lifecycle used by `isUnlockPopupWindow` so an unmarked opener that
 * never answers is not treated as unlock.
 */
export function requestUnlockPopupChallenge(): Promise<UnlockPopupChallengeMessage | null> {
  if (!hasSameOriginOpener()) {
    captureUnlockPopupClassification(null);
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;

    const finish = (value: UnlockPopupChallengeMessage | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timeoutId);
      captureUnlockPopupClassification(value);
      resolve(value);
    };

    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== window.opener) return;
      const parsed = parseUnlockPopupChallenge(event.data);
      if (parsed === null) return;
      rememberUnlockPopupNonce(parsed.nonce);
      finish(parsed);
    };

    const timeoutId = window.setTimeout(() => {
      finish(null);
    }, UNLOCK_POPUP_HANDSHAKE_TIMEOUT_MS);

    window.addEventListener('message', onMessage);

    try {
      const opener = window.opener;
      if (opener === null || opener === undefined) {
        finish(null);
        return;
      }
      postUnlockPopupMessage(opener, { type: UNLOCK_POPUP_HELLO_TYPE });
    } catch {
      finish(null);
    }
  });
}

export function subscribeUnlockPopup(
  popup: Window,
  expected: UnlockPopupIdentity,
  handlers: {
    onSuccess: (tokens: { accessToken: string; refreshToken: string }) => void;
    onFailure: () => void;
  },
  proof: { nonce: string; refreshToken: string },
): () => void {
  let settled = false;

  const cleanup = () => {
    window.removeEventListener('message', onMessage);
    window.clearInterval(closedPoll);
    window.clearTimeout(timeoutId);
  };

  const settle = (apply: () => void) => {
    if (settled) return;
    settled = true;
    cleanup();
    apply();
  };

  const replyWithChallenge = () => {
    try {
      postUnlockPopupMessage(popup, {
        type: UNLOCK_POPUP_CHALLENGE_TYPE,
        nonce: proof.nonce,
        sessionFamilyId: expected.sessionFamilyId,
        lockGeneration: expected.lockGeneration,
        userId: expected.userId,
        tenantId: expected.tenantId,
        sessionEpoch: expected.sessionEpoch,
      });
    } catch {
      // Popup navigated away; it will hello again after it returns.
    }
  };

  const onMessage = (event: MessageEvent<unknown>) => {
    if (event.origin !== window.location.origin) return;
    if (event.source !== popup) return;
    if (parseUnlockPopupHello(event.data) !== null) {
      replyWithChallenge();
      return;
    }
    const parsed = parseUnlockPopupMessage(event.data);
    if (parsed === null) return;
    if (parsed.status === 'error') {
      settle(() => handlers.onFailure());
      return;
    }
    if (parsed.nonce !== proof.nonce) {
      settle(() => handlers.onFailure());
      return;
    }
    if (!identitiesMatch(parsed, expected)) {
      settle(() => handlers.onFailure());
      return;
    }
    if (!refreshTokensMatch(parsed.refreshToken, proof.refreshToken)) {
      settle(() => handlers.onFailure());
      return;
    }
    settle(() =>
      handlers.onSuccess({
        accessToken: parsed.accessToken,
        refreshToken: parsed.refreshToken,
      }),
    );
  };

  const closedPoll = window.setInterval(() => {
    try {
      if (popup.closed) {
        settle(() => handlers.onFailure());
      }
    } catch {
      settle(() => handlers.onFailure());
    }
  }, 400);

  const timeoutId = window.setTimeout(() => {
    try {
      popup.close();
    } catch {
      // Already gone.
    }
    settle(() => handlers.onFailure());
  }, UNLOCK_POPUP_TIMEOUT_MS);

  window.addEventListener('message', onMessage);

  return () => {
    settle(() => undefined);
  };
}
